import { ExternalLink } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { type FragmentOf, type SiteLocale, readFragment } from "~/lib/graphql";

import * as fragment from "./graphql";

function LpCard({ card }: { card: any }) {
  const getCardLink = () => {
    if (card.externalUrl) {
      return card.externalUrl;
    }

    if (card.page) {
      switch (card.page.__typename) {
        case "LandingDetailPageRecord":
          return `/${card.page.slug}`;
        case "LandingFrontPageRecord":
          return card.page.slug ? `/${card.page.slug}` : "/";
        default:
          return null;
      }
    }

    return card.slug ? `/${card.slug}` : null;
  };

  const hasLink = !!(card.externalUrl || card.page || card.slug);
  const isExternal = !!card.externalUrl;
  const link = getCardLink();

  const getCtaLabel = () => {
    if (card.ctaLabel) {
      return card.ctaLabel;
    }

    if (card.page) {
      return card.page.title || card.page.name || (isExternal ? "Learn More" : "Read More");
    }

    return isExternal ? "Learn More" : "Read More";
  };

  const ctaLabel = getCtaLabel();

  const cardContent = (
    <Card
      className={`h-full bg-white transition-all duration-200 ${hasLink ? "group hover:-translate-y-1 hover:shadow-lg" : ""}`}
    >
      {card.image && (
        <div className="relative aspect-video overflow-hidden">
          <Image
            src={card.image.url}
            alt={card.image.alt || card.title || "Card image"}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
      )}
      <CardHeader>
        {card.title && (
          <CardTitle
            className={hasLink ? "group-hover:text-primary transition-colors" : ""}
          >
            {card.title}
          </CardTitle>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between">
        {card.text && (
          <CardDescription className="text-muted-foreground mb-4">
            {card.text}
          </CardDescription>
        )}
        {hasLink && (
          <Button variant="outline" className="b- mt-auto w-fit" asChild={!isExternal}>
            {isExternal ? (
              <span className="flex items-center gap-2">
                {ctaLabel}
                <ExternalLink className="h-4 w-4" />
              </span>
            ) : (
              ctaLabel
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (!hasLink) {
    return cardContent;
  }

  if (isExternal) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="block h-full">
        {cardContent}
      </a>
    );
  }

  return (
    <Link href={link} className="block h-full">
      {cardContent}
    </Link>
  );
}

export function LpCardBlock(props: {
  record: FragmentOf<typeof fragment.LpCardBlockFragment>;
  locale: SiteLocale;
}) {
  const record = readFragment(fragment.LpCardBlockFragment, props.record);

  const getGridClasses = () => {
    switch (record.gridColumns) {
      case "1":
        return "grid-cols-1";
      case "2":
        return "grid-cols-1 md:grid-cols-2";
      case "3":
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      case "4":
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
      case "5":
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5";
      case "6":
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6";
      default:
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    }
  };

  if (!record.card || record.card.length === 0) {
    return null;
  }

  return (
    <div className="group container mx-auto mb-10">
      <div className={`grid gap-6 ${getGridClasses()}`}>
        {record.card.map((card) => (
          <LpCard key={card.id} card={card} />
        ))}
      </div>
    </div>
  );
}
